/** Click-to-open Racket Finder. The page, URL filters and scroll position stay underneath.
 * Only the existing finder and optional kit payload are imported from the canonical page;
 * scripts/apps in that response are never executed. See features/product-finder.md.
 */
class ProductFinderDialog extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.dialog = this.querySelector('dialog');
    this.body = this.querySelector('[data-pf-overlay-body]');
    this.mount = this.querySelector('[data-pf-overlay-mount]');
    this.loading = this.querySelector('[data-pf-loading]');
    this.error = this.querySelector('[data-pf-load-error]');
    this.querySelector('[data-pf-close]').addEventListener('click', () => this.close());
    this.querySelector('[data-pf-retry]').addEventListener('click', () => this.load());
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    // Safari pans its visual viewport when the keyboard opens. Follow that viewport
    // so the header/Close remain visible while only the quiz body scrolls.
    window.visualViewport?.addEventListener('resize', () => this.fitViewport());
    window.visualViewport?.addEventListener('scroll', () => this.fitViewport());
    document.addEventListener('click', (event) => {
      const link = event.target.closest?.('a[href]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.hasAttribute('download') || (link.target && link.target !== '_self') || link.hasAttribute('data-pf-page-fallback')) return;
      const url = new URL(link.href, location.href);
      const destination = new URL(this.dataset.finderUrl, location.origin);
      if (url.origin !== location.origin || url.pathname.replace(/\/$/, '') !== destination.pathname.replace(/\/$/, '')) return;
      if (!this.dialog.showModal || window.Shopify?.designMode) return;
      event.preventDefault();
      this.open(link);
    });
    window.addEventListener('popstate', () => {
      if (history.state?.pusaRacketFinder) this.open(null, false);
      else this.hide();
      this.resolveClose?.();
      this.resolveClose = null;
      this.closing = null;
    });
    // A refresh / browser return to the quiz history entry can resume stored answers.
    if (history.state?.pusaRacketFinder) this.open(null, false);
  }

  async open(trigger, addHistory = true) {
    if (this.closing) await this.closing;
    if (this.dialog.open) return;
    const drawer = trigger?.closest('header-drawer');
    this.returnFocus = drawer?.querySelector('summary') || trigger || document.activeElement;
    drawer?.close?.();
    this.scrollPosition = { x: window.scrollX, y: window.scrollY };
    this.bodyStyle = ['position', 'top', 'left', 'width'].map((key) => [key, document.body.style.getPropertyValue(key), document.body.style.getPropertyPriority(key)]);
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition.y}px`;
    document.body.style.left = `-${this.scrollPosition.x}px`;
    document.body.style.width = '100%';
    this.dialog.showModal();
    this.fitViewport();
    if (addHistory && !history.state?.pusaRacketFinder) {
      history.pushState({ ...history.state, pusaRacketFinder: true }, '', location.href);
    }
    this.querySelector('[data-pf-close]').focus({ preventScroll: true });
    await this.load();
  }

  fitViewport() {
    const viewport = window.visualViewport;
    if (!viewport || !this.dialog.open) return;
    this.dialog.style.setProperty('--pf-viewport-top', `${viewport.offsetTop}px`);
    this.dialog.style.setProperty('--pf-viewport-height', `${viewport.height}px`);
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (!active?.matches('input, textarea') || !this.body.contains(active)) return;
      const field = active.getBoundingClientRect();
      const body = this.body.getBoundingClientRect();
      if (field.bottom > body.bottom - 16) this.body.scrollTop += field.bottom - body.bottom + 16;
      else if (field.top < body.top + 16) this.body.scrollTop -= body.top + 16 - field.top;
    });
  }

  close() {
    if (this.closing) return this.closing;
    this.hide();
    if (!history.state?.pusaRacketFinder) return Promise.resolve();
    this.closing = new Promise((resolve) => { this.resolveClose = resolve; });
    history.back();
    return this.closing;
  }

  hide() {
    if (!this.dialog.open) return;
    this.dialog.close();
    for (const [key, value, priority] of this.bodyStyle || []) {
      if (value) document.body.style.setProperty(key, value, priority);
      else document.body.style.removeProperty(key);
    }
    window.scrollTo({ left: this.scrollPosition.x, top: this.scrollPosition.y, behavior: 'instant' });
    if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
  }

  async load() {
    if (this.finder) return;
    if (this.request) return this.request;
    this.loading.hidden = false;
    this.error.hidden = true;
    this.request = this.loadFinder().catch(() => {
      this.loading.hidden = true;
      this.error.hidden = false;
    }).finally(() => { this.request = null; });
    return this.request;
  }

  async loadFinder() {
    const controller = new AbortController();
    let timer;
    try {
      // Fetch once, only after intent. Preserve the configured page's collection, offers,
      // exclusions and attribution settings rather than rendering a second default section.
      const deadline = new Promise((_, reject) => {
        timer = setTimeout(() => { controller.abort(); reject(new Error('Finder load timed out')); }, 12000);
      });
      const work = async () => {
        const url = new URL(this.dataset.finderUrl, location.origin);
        const preview = new URL(location.href).searchParams.get('preview_theme_id') ||
          document.querySelector('results-list[data-cll-view="cll-detail"]')?.dataset.cllTheme;
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Finder unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        // v2 (<racket-finder>) or classic (<product-finder-app>), whichever the page's section renders.
        const source = doc.querySelector('racket-finder, product-finder-app');
        if (!source?.querySelector('[data-pf-products]')) throw new Error('Finder missing');
        source.querySelectorAll('script:not([type="application/json"])').forEach((script) => script.remove());
        source.removeAttribute('id');
        source.removeAttribute('data-pf-init');
        const finder = document.importNode(source, true);
        await this.ensureKit(doc);
        if (!customElements.get('product-finder-app')) await import(this.dataset.finderScript);
        if (source.localName === 'racket-finder' && !customElements.get('racket-finder')) {
          await import(this.dataset.finderV2Script);
          await customElements.whenDefined('racket-finder');
        }
        if (controller.signal.aborted) throw new Error('Finder load cancelled');
        this.mount.replaceChildren(finder);
        this.finder = finder;
        // The dialog shell adopts the finder's color scheme (white for v2, charcoal for classic).
        const scheme = [...finder.classList].find((name) => name.startsWith('color-scheme-'));
        if (scheme) {
          this.dialog.classList.remove(...[...this.dialog.classList].filter((name) => name.startsWith('color-scheme-')));
          this.dialog.classList.add(scheme);
        }
        this.loading.hidden = true;
        // The visitor already asked to find a racket: start with the first question.
        if (finder.dataset.pfPanel === 'intro') finder.start();
      };
      await Promise.race([work(), deadline]);
    } finally {
      clearTimeout(timer);
    }
  }

  async ensureKit(doc) {
    if (document.querySelector('kit-upsell-dialog')) return;
    const kit = doc.querySelector('[data-kit-upsell-mount]');
    if (!kit) return; // Existing finder still has an Added / View cart fallback.
    kit.querySelectorAll('script').forEach((script) => script.remove());
    if (!customElements.get('kit-upsell-dialog')) await import(this.dataset.kitScript);
    if (!document.querySelector('kit-upsell-dialog')) document.body.append(document.importNode(kit, true));
  }
}

if (!customElements.get('product-finder-dialog')) customElements.define('product-finder-dialog', ProductFinderDialog);
